import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControlLabel,
  Radio,
  RadioGroup,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Notifications,
  Settings as SettingsIcon,
  Timer,
} from "@mui/icons-material";
import { getSetting, updateSessionTimeoutSetting } from "apis";
import NotificationSettings from "components/settings/NotificationSettings";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { fetchSetting, SettingState } from "store/features/setting";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

const SESSION_OPTION_VALUES = ["10m", "2h", "1d", "7d", "30d"];

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [sessionTimeout, setSessionTimeout] = useState("10m");
  const [savingSession, setSavingSession] = useState(false);
  const { loaded, sessionTimeout: timeout } = useSelector<
    RootState,
    SettingState
  >((store) => store.setting);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!loaded) {
      getSetting()
        .then(({ data }) => dispatch(fetchSetting(data)))
        .catch(() => {});
    }
  }, [loaded, dispatch]);

  useEffect(() => {
    if (loaded && timeout) setSessionTimeout(timeout);
  }, [loaded, timeout]);

  const notificationRef = useRef<{ saveSettings: () => void }>();

  const saveSessionTimeoutSettings = () => {
    setSavingSession(true);
    updateSessionTimeoutSetting(sessionTimeout)
      .then((data) => {
        dispatch(fetchSetting(data));
        enqueueSnackbar(t("setting.session_updated"), { variant: "success" });
      })
      .catch(() => enqueueSnackbar("Failed", { variant: "error" }))
      .finally(() => setSavingSession(false));
  };

  return (
    <>
      <Toolbar
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          gap: 1.5,
        }}
      >
        <SettingsIcon sx={{ color: "primary.main", fontSize: 22 }} />
        <Typography variant="h6" fontWeight={600}>
          {t("setting.label")}
        </Typography>
      </Toolbar>

      <Container sx={{ py: 2, maxWidth: 600 }} maxWidth={false}>
        {/* Notifications */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: "primary.main",
                }}
              >
                <Notifications sx={{ fontSize: 18 }} />
              </Box>
              <Typography variant="subtitle2" fontWeight={700}>
                {t("notifications.label")}
              </Typography>
            </Box>
            <NotificationSettings ref={notificationRef} />
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="contained"
                onClick={() => notificationRef.current?.saveSettings()}
              >
                {t("buttons.save")}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Session Timeout */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: "primary.main",
                }}
              >
                <Timer sx={{ fontSize: 18 }} />
              </Box>
              <Typography variant="subtitle2" fontWeight={700}>
                {t("sessions.timeout")}
              </Typography>
            </Box>
            <RadioGroup
              value={sessionTimeout}
              onChange={(_, v) => setSessionTimeout(v)}
            >
              {SESSION_OPTION_VALUES.map((val) => (
                <FormControlLabel
                  key={val}
                  value={val}
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2">
                      {t(`sessions.${val}`)}
                    </Typography>
                  }
                  sx={{
                    mx: 0,
                    py: 0.5,
                    px: 1.5,
                    borderRadius: 1,
                    mb: 0.5,
                    ...(sessionTimeout === val && {
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                    }),
                  }}
                />
              ))}
            </RadioGroup>
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="contained"
                onClick={saveSessionTimeoutSettings}
                disabled={savingSession}
              >
                {savingSession ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  t("buttons.save")
                )}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </>
  );
};

export default Settings;
